import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { Case } from '@app/shared/models/case';
import { BaseHttpService, Create, Delete, GetById, Patch, UriBuilderParams } from './base/base-http.service';
import { HttpServiceBundle } from './base/http-service-bundle.service';
import { map } from 'rxjs/operators';
import { CaseOverviewMetaData } from '@app/shared/models/case-overview-meta-data';
import { DRResponse, SearchResponse } from '@app/shared/models/response';
import { SearchParams, SearchSuggestionList } from '@app/shared/models/search';
import { VehicleDamageMetaResponse } from '@app/shared/models/vehicle-damage-meta';
import { DrQueryParams, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { CaseCreationDialogComponent } from '@app/modules/case/components/case-creation-dialog/case-creation-dialog.component';
import { OfficeService, OfficeUriBuilderParams } from '@app/core/services/office.service';
import { Office } from '@app/shared/models/office';
import { Module } from '@app/core/authentication/permission';
import { User } from '@app/shared/models/user';
import { WorkflowService, WorkflowUriBuilderParams } from '@app/core/services/workflow/workflow.service';
import { Workflow, WorkflowTargets } from '@app/shared/models/workflow';
import { BaseWithIdList } from '@app/shared/models/list';
import { HasId } from '@app/shared/models/base';
import { AuthenticationService } from '@app/core/authentication';


export class CaseUriBuilderParams extends UriBuilderParams<Case> {

  constructor(caseX?: Case | string) {
    super(caseX);
  }

  public buildUri(): string {
    return this.simpleUriBuild(BackendUriUnits.CASES);
  }
}

@Injectable({
  providedIn: 'root'
})
export class CaseService extends BaseHttpService<Case, CaseUriBuilderParams>
                         implements GetById<Case, CaseUriBuilderParams>, Create<Case, CaseUriBuilderParams>, Patch<Case, CaseUriBuilderParams>, Delete<Case, CaseUriBuilderParams> {

  constructor(httpServiceBundle: HttpServiceBundle,
              public officeService: OfficeService,
              public workflowService: WorkflowService,
              public authenticationService: AuthenticationService) {
    super(Case, httpServiceBundle);
  }

  // BASE METHODS ****************************************************************************************

  public getById(uriParams: CaseUriBuilderParams): Observable<Case> {
    return this.getBaseById(uriParams);
  }

  public create(uriParams: CaseUriBuilderParams, caseToCreate: Case): Observable<Case> {
    return this.createBase(uriParams, caseToCreate);
  }

  public patch(uriParams: CaseUriBuilderParams, data: OpData[]): Observable<Case> {
    return this.patchBase(uriParams, data);
  }

  public patchAndUpdate(uriParams: CaseUriBuilderParams, caseToUpdate: Case, data: OpData[], queryParams: DrQueryParams): Observable<Case> {
    return this.patchAndUpdateBase(uriParams, caseToUpdate, data, queryParams);
  }

  public delete(uriParams: CaseUriBuilderParams): Observable<boolean> {
    return this.deleteBase(uriParams);
  }


  // ADD-ON METHODS *************************************************************************************

  public getCasesOverviewMeta(): Observable<CaseOverviewMetaData> {
    return this.http.get<DRResponse<CaseOverviewMetaData>>(`${this.uri}/${BackendUriUnits.CASES}/overview/metadata`)
                    .pipe(map(res => new CaseOverviewMetaData(res.data)));
  }

  public getCasesOverview(searchParams: SearchParams): Observable<SearchResponse> {
    return this.http.get<DRResponse<SearchResponse>>(`${this.uri}/${BackendUriUnits.CASES}/overview`, { params: searchParams.getAsHttpParams()})
                    .pipe(map(res => new SearchResponse(res.data)));
  }

  public getCasesOverviewSuggestions(suggestionInput: string): Observable<SearchSuggestionList> {
    return this.http.get<DRResponse<SearchSuggestionList>>(`${this.uri}/${BackendUriUnits.CASES}/overview/suggestions`, { params: { search: suggestionInput }})
                    .pipe(map(res => new SearchSuggestionList(res.data)));
  }

  public massCaseDeletion(deletionData: OpData[]): Observable<any> {
    return this.http.patch<DRResponse<any>>(`${this.uri}/${BackendUriUnits.CASES}`, deletionData)
                    .pipe(map(res => res.data ));
  }

  public getDamageMetaData(uriParams: CaseUriBuilderParams): Observable<VehicleDamageMetaResponse> {
    const uri = `${this.uri}${uriParams.buildUri()}/available_damage_metadata`;
    return this.http.get<DRResponse<VehicleDamageMetaResponse>>(uri)
                    .pipe(map(res => new VehicleDamageMetaResponse(res.data)));
  }

  public createByWorkflow(caseX: Case): Observable<HasId> {
    return this.workflowService.createTargetByWorkflow(WorkflowTargets.SMP_CASE, caseX);
  };


  // CEPH Check **********************************

  public validateCaseContentStoreModel(caseId: string, contentStoreRepoModel: string) {
    let clientContentStoreRepoModel = this.authenticationService.currentUserValue.client.contentstoreRepositoryModel;
    if ((!clientContentStoreRepoModel || clientContentStoreRepoModel === 'JCR') && contentStoreRepoModel === 'CEPH') {
      return this.convertToJCRContentStoreModel(new CaseUriBuilderParams(caseId));
    } else {
      return of(true);
    }
  };

  public convertToJCRContentStoreModel(uriParams: CaseUriBuilderParams): Observable<any> {
    const uri = `${this.uri}${uriParams.buildUri()}/ceph_check`;
    return this.http.post<DRResponse<any>>(uri, null)
                    .pipe();
  }

  // DIALOGS ********************************************************************************************

  public openCaseCreationDialog(caseX: Case, user: User, officesOnly?: boolean, removeCancelBtn?: boolean): Observable<{ office: Office, workflow: Workflow }> {
    const subject = new Subject<{ office: Office, workflow: Workflow }>();

    const observables: Observable<any>[] = [
      this.officeService.query(new OfficeUriBuilderParams(), null, 'offices')
    ];

    if (!officesOnly && user.hasModules(Module.MODULE_WORKFLOW)) {
      observables.push(this.workflowService.query(new WorkflowUriBuilderParams(WorkflowTargets.SMP_CASE)));
    }

    forkJoin(observables).subscribe((results: any) => {
      const officesList = results[0];
      const workflowList = results.length > 1 ? results[1] : new BaseWithIdList(Workflow.getSubTypeConstructor, {});
      workflowList.items = workflowList.items.filter(workflow => workflow.active);

      if (officesList.items.length === 1 && (!workflowList || !workflowList.items.length)) {
        subject.next({ office: officesList.items[0], workflow: null });
        subject.complete();
      } else {
        const dialogRef = this.dialog.open(CaseCreationDialogComponent, {
          width: '400px',
          data: {
            case: caseX,
            removeCancelBtn: removeCancelBtn,
            offices: officesList,
            workflows: workflowList
          }
        });

        dialogRef.afterClosed()
                 .subscribe(dialogResult => {
          subject.next(dialogResult);
          subject.complete();
        });
      }
    });

    return subject;
  }
}
